import { Injectable, inject } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { TextAttachmentsConfig } from '@shared/components/text-attachments-input/models/text-attachments-config';
import {
  AttachmentLibraryDialogResult,
  AttachmentLibraryModalComponent
} from '../components/attachment-library-modal/attachment-library-modal.component';
import {
  QuillAttachmentSource,
  QuillAttachmentSourcePickerComponent
} from '../components/quill-attachment-source-picker/quill-attachment-source-picker.component';
import { ProjectAttachmentLibraryItem } from '../models/project-attachment-library.model';

export type QuillAttachmentSelection = { type: 'device' } | { type: 'library'; items: ProjectAttachmentLibraryItem[] };

@Injectable({ providedIn: 'root' })
export class QuillAttachmentSourcePickerService {
  private readonly dialog = inject(MatDialog);
  private readonly overlay = inject(Overlay);

  select(
    projectId: number | null,
    attachmentsConfig: TextAttachmentsConfig,
    origin?: HTMLElement | null
  ): Observable<QuillAttachmentSelection | null> {
    if (!projectId) return of({ type: 'device' });

    return new Observable<QuillAttachmentSource | null>((subscriber) => {
      if (!origin) {
        subscriber.next(null);
        subscriber.complete();
        return;
      }

      const overlayRef = this.overlay.create({
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        positionStrategy: this.overlay
          .position()
          .flexibleConnectedTo(origin)
          .withFlexibleDimensions(false)
          .withPush(true)
          .withPositions([
            { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
            { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
            { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
          ])
      });
      const picker = overlayRef.attach(new ComponentPortal(QuillAttachmentSourcePickerComponent));
      const close = (source: QuillAttachmentSource | null) => {
        overlayRef.dispose();
        subscriber.next(source);
        subscriber.complete();
      };
      const sourceSub = picker.instance.sourceSelected.subscribe(close);
      const backdropSub = overlayRef.backdropClick().pipe(take(1)).subscribe(() => close(null));

      return () => {
        sourceSub.unsubscribe();
        backdropSub.unsubscribe();
        overlayRef.dispose();
      };
    })
      .pipe(
        switchMap((source) => {
          if (source === 'device') return of({ type: 'device' } as QuillAttachmentSelection);
          if (source !== 'library') return of(null);

          return this.dialog
            .open(AttachmentLibraryModalComponent, {
              width: '1120px',
              height: 'calc(100vh - 96px)',
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '760px',
              autoFocus: false,
              disableClose: true,
              data: { projectId, attachmentsConfig, attachedIds: [], maxSelectable: null }
            })
            .afterClosed()
            .pipe(
              switchMap((result?: AttachmentLibraryDialogResult) =>
                of(result?.items?.length ? ({ type: 'library', items: result.items } as QuillAttachmentSelection) : null)
              )
            );
        })
      );
  }
}
